import React, { useState, useEffect } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './QuillEditor.css';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

// Quill editor modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'font': [] }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub'}, { 'script': 'super' }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link', 'image', 'video'],
    ['blockquote', 'code-block'],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'indent',
  'align',
  'link', 'image', 'video',
  'blockquote', 'code-block'
];

const EmailNotificationTemplate = () => {
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [filters, setFilters] = useState({
    templateName: '',
  });

  const [formData, setFormData] = useState({
    templateName: '',
    subject: '',
    body: '',
  });

  // Fetch all email templates
  const fetchEmailTemplates = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      if (filters.templateName) queryParams.append('templateName', filters.templateName);

      const response = await fetch(`${API_BASE_URL}/email-templates?${queryParams.toString()}`);
      const data = await response.json();

      if (data.success) {
        setEmailTemplates(data.data.emailTemplates);
      } else {
        showSnackbar(data.message || 'Failed to fetch email templates', 'error');
      }
    } catch (err) {
      showSnackbar('Error fetching email templates: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Show snackbar notification
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    fetchEmailTemplates();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    fetchEmailTemplates();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      templateName: '',
    });
    setTimeout(() => fetchEmailTemplates(), 100);
  };

  // Open modal for creating new template
  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormData({
      templateName: '',
      subject: '',
      body: '',
    });
    setShowModal(true);
  };

  // Open modal for editing template
  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      templateName: template.templateName || '',
      subject: template.subject || '',
      body: template.body || '',
    });
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setShowHtmlSource(false);
    setFormData({
      templateName: '',
      subject: '',
      body: '',
    });
  };

  // Save email template (create or update)
  const saveEmailTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingTemplate
        ? `${API_BASE_URL}/email-templates/${editingTemplate.id}`
        : `${API_BASE_URL}/email-templates`;

      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        showSnackbar(data.message, 'success');
        closeModal();
        fetchEmailTemplates();
      } else {
        showSnackbar(data.message || 'Failed to save email template', 'error');
      }
    } catch (err) {
      showSnackbar('Error saving email template: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Delete email template
  const deleteEmailTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this email template?')) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/email-templates/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        showSnackbar('Email template deleted successfully', 'success');
        fetchEmailTemplates();
      } else {
        showSnackbar(data.message || 'Failed to delete email template', 'error');
      }
    } catch (err) {
      showSnackbar('Error deleting email template: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'white', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight="600" color="primary">
              Email Template Management
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateModal}
            size="large"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1.5,
              boxShadow: 2
            }}
          >
            Create New Template
          </Button>
        </Box>

        {/* Filters Section */}
        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="500">
                Filters
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8} md={6}>
                <TextField
                  fullWidth
                  label="Template Name"
                  name="templateName"
                  value={filters.templateName}
                  onChange={handleFilterChange}
                  placeholder="Search by template name"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Box sx={{ display: 'flex', gap: 1, height: '100%' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={applyFilters}
                    sx={{ textTransform: 'none' }}
                  >
                    Apply
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={resetFilters}
                    startIcon={<RefreshIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Paper>

      {/* Email Templates Table */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && emailTemplates.length === 0 && (
          <Box sx={{ textAlign: 'center', p: 6 }}>
            <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No email templates found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create your first email template to get started
            </Typography>
          </Box>
        )}

        {!loading && emailTemplates.length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Template Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Subject</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600, minWidth: 300 }}>Body</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailTemplates.map((template, index) => (
                  <TableRow
                    key={template.id}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      backgroundColor: index % 2 === 0 ? 'background.default' : 'white',
                    }}
                  >
                    <TableCell>
                      <Chip label={template.id} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {template.templateName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={template.subject} arrow>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {template.subject}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip 
                        title={
                          <div 
                            dangerouslySetInnerHTML={{ __html: template.body || '' }}
                            style={{ maxWidth: 400, maxHeight: 300, overflow: 'auto' }}
                          />
                        } 
                        arrow
                      >
                        <Box
                          sx={{
                            maxWidth: 300,
                            maxHeight: 60,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            '& *': {
                              fontSize: '0.875rem',
                              margin: 0,
                              padding: 0,
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: template.body || '-' }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEditModal(template)}
                            sx={{ '&:hover': { backgroundColor: 'primary.light', color: 'white' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteEmailTemplate(template.id)}
                            sx={{ '&:hover': { backgroundColor: 'error.light', color: 'white' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Modal */}
      <Dialog
        open={showModal}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon />
            <Typography variant="h6" fontWeight="600">
              {editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
            </Typography>
          </Box>
        </DialogTitle>
        <form onSubmit={saveEmailTemplate}>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Template Name"
                  name="templateName"
                  value={formData.templateName}
                  onChange={handleInputChange}
                  placeholder="e.g., Welcome Email, OTP Verification"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="Email subject line"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight="500">
                    Email Body (HTML) <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowHtmlSource(!showHtmlSource)}
                    sx={{ textTransform: 'none' }}
                  >
                    {showHtmlSource ? '📝 Rich Editor' : '📄 HTML Source'}
                  </Button>
                </Box>
                
                {showHtmlSource ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={12}
                    value={formData.body}
                    onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
                    placeholder="<html><body>Your HTML code here...</body></html>"
                    InputProps={{
                      sx: { 
                        fontFamily: 'monospace',
                        fontSize: '13px'
                      }
                    }}
                    helperText="Direct HTML editing mode"
                  />
                ) : (
                  <>
                    <Box
                      sx={{
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: 1,
                        '& .ql-container': {
                          minHeight: '300px',
                          fontSize: '14px',
                        },
                        '& .ql-editor': {
                          minHeight: '300px',
                        },
                        '&:hover': {
                          borderColor: 'rgba(0, 0, 0, 0.87)',
                        },
                      }}
                    >
                      <ReactQuill
                        theme="snow"
                        value={formData.body}
                        onChange={(value) => setFormData(prev => ({ ...prev, body: value }))}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Write your email content here..."
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Use the toolbar to format your email with headings, lists, colors, and more
                    </Typography>
                  </>
                )}
              </Grid>

            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, gap: 1 }}>
            <Button
              onClick={closeModal}
              variant="outlined"
              sx={{ textTransform: 'none', px: 3 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ textTransform: 'none', px: 3 }}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailNotificationTemplate;
